// Public Privacy Policy page.
//
// Layout matches the design system in privacy-policy.html:
//   • Dark-ink hero with eyebrow / two-line display title / lede
//   • Doc metadata strip (ID / Version / Effective / Reviewed)
//   • 12-col Grid body — md=3 sticky TOC + md=9 article column
//   • Numbered sections rendered from data.sections[]
//   • Grievance Officer card injected via {{grievance_card}} marker
//
// All text comes from /api/legal/privacy (admin-curated). The bulk of
// the styling is scoped to `.legal-shell` so the rest of the site's
// theme is untouched.

import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Skeleton from '@mui/material/Skeleton';
import { useGetPrivacyPolicyQuery } from '../store/siteContentApi';
import {
  UI, DISPLAY, wrap, phero, pheroIn, hXl, eyebrow as eyebrowSx, card as cardSx,
} from '../styles/tl5';

import renderLegalMarkdown, { GRIEVANCE_MARKER } from '../utils/legalMarkdown';

const MONO = "'JetBrains Mono', ui-monospace, Menlo, monospace";

// ─── Design tokens (mirror privacy-policy.html) ────────────────
// Palette retargeted to v5; the key names stay so every `C.x` in the
// file — hero, sections, grievance card, prose — moves in one step.
const C = {
  teal:    '#157A69',
  gold:    '#9A5200',
  ink:     '#0B2B27',
  inkSoft: '#23463F',
  paper:   '#FFFFFF',
  paper2:  '#EEF6F3',
  line:    '#D5E5E0',
  muted:   '#4A605B',
};

// Convert any vaguely date-like value to a display string. The admin
// stores effective_date as free text already (e.g. "03 May 2026"), so
// we only reformat when it parses as an ISO date.
const toDisplayDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Grievance officer card (Section 15) ───────────────────────
const GrievanceCard = ({ officer }) => (
  <Box
    className="legal-grievance-card"
    sx={{
      bgcolor: C.ink,
      color:   C.paper,
      px:      { xs: 3, md: 4 },
      py:      { xs: 4, md: 4.5 },
      borderRadius: '6px',
      mt: 2,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: -40, right: -40,
        width: 180, height: 180,
        bgcolor: C.teal,
        borderRadius: '50%',
        opacity: 0.15,
      },
    }}
  >
    <Box
      sx={{
        position: 'relative',
        fontFamily: DISPLAY,
        color: C.gold,
        fontSize: 24,
        letterSpacing: '2px',
        mb: 2,
      }}
    >
      Grievance Officer / DPO
    </Box>

    <Grid container spacing={2.5} sx={{ position: 'relative', width: '100%', m: 0, '& > .MuiGrid-item': { pl: 0 } }}>
      {[
        ['Name',           officer.name],
        ['Designation',    officer.designation],
        ['Email',          officer.email,   officer.email ? `mailto:${officer.email}` : null],
        ['Phone',          officer.phone],
        ['Postal Address', [officer.address, officer.pin].filter(Boolean).join(' — ')],
        ['Working Hours',  officer.working_hours],
      ].map(([label, value, href]) => (
        value ? (
          <Grid item xs={12} sm={6} key={label}>
            <Box
              sx={{
                fontFamily: MONO,
                fontSize: 10,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                color: C.teal,
                mb: 0.5,
              }}
            >
              {label}
            </Box>
            <Box sx={{ fontSize: 14, color: C.paper, lineHeight: 1.5 }}>
              {href ? (
                <a href={href} style={{ color: C.paper, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                  {value}
                </a>
              ) : value}
            </Box>
          </Grid>
        ) : null
      ))}
    </Grid>
  </Box>
);

// ─── One numbered section ──────────────────────────────────────
const PolicySection = ({ section, vars, officer }) => {
  const html = useMemo(
    () => renderLegalMarkdown(section.content_md, vars),
    [section.content_md, vars],
  );

  // Split rendered HTML on the grievance marker so we can inject a real
  // React card between the two halves (admin keeps writing markdown).
  const [before, after] = html.includes(GRIEVANCE_MARKER)
    ? html.split(GRIEVANCE_MARKER)
    : [html, null];

  return (
    <Box
      component="section"
      id={section.slug}
      sx={{
        mb: 8,
        scrollMarginTop: '96px',
      }}
    >
      <Box
        sx={{
          fontFamily: MONO,
          fontSize: 13,
          color: C.gold,
          letterSpacing: '2px',
          mb: 1,
        }}
      >
        {section.number}
      </Box>
      <Box
        component="h2"
        sx={{
          fontFamily: DISPLAY,
          fontSize: { xs: 32, md: 38 },
          letterSpacing: '1.5px',
          color: C.ink,
          m: 0,
          mb: 3,
          lineHeight: 1.05,
        }}
      >
        {section.title}
      </Box>

      <Box className="legal-prose" dangerouslySetInnerHTML={{ __html: before }} />
      {after !== null && (
        <>
          <GrievanceCard officer={officer} />
          <Box className="legal-prose" sx={{ mt: 3 }} dangerouslySetInnerHTML={{ __html: after }} />
        </>
      )}
    </Box>
  );
};

// ─── Page-level prose styles (scoped to .legal-shell) ──────────
const proseSx = {
  '& .legal-prose': {
    fontFamily: "'DM Sans', sans-serif",
    fontSize:   { xs: 15, md: 16 },
    lineHeight: 1.65,
    color: C.inkSoft,
  },
  '& .legal-prose p': {
    m: 0, mb: 1.75,
  },
  '& .legal-prose .legal-lede': {
    fontSize: { xs: 16, md: 17 },
    lineHeight: 1.6,
    color: C.inkSoft,
    borderTop: `2px solid ${C.ink}`,
    pt: 2.5,
    mb: 3,
  },
  '& .legal-prose strong': { color: C.ink, fontWeight: 700 },
  '& .legal-prose a': {
    color: C.teal,
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
    textDecorationThickness: '1px',
  },
  '& .legal-prose a:hover': { color: C.ink },
  '& .legal-prose h3': {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 17,
    fontWeight: 700,
    color: C.ink,
    mt: 3.5,
    mb: 1,
    letterSpacing: '-0.1px',
  },
  '& .legal-prose ul, & .legal-prose ol': { pl: 2.75, my: 1, mb: 2 },
  '& .legal-prose li':  { mb: 0.75 },
  '& .legal-prose code': {
    bgcolor: 'rgba(0,0,0,0.05)',
    border: `1px solid ${C.line}`,
    borderRadius: 1,
    px: 0.75,
    py: 0.125,
    fontFamily: MONO,
    fontSize: '0.9em',
  },

  // Tables
  '& .legal-prose .legal-table': {
    width: '100%',
    borderCollapse: 'collapse',
    my: 2,
    fontSize: 14,
  },
  '& .legal-prose .legal-table th, & .legal-prose .legal-table td': {
    border: `1px solid ${C.line}`,
    p: '12px 14px',
    textAlign: 'left',
    verticalAlign: 'top',
  },
  '& .legal-prose .legal-table th': {
    bgcolor: C.ink,
    color: C.paper,
    fontFamily: MONO,
    fontSize: 11,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    fontWeight: 400,
  },
  '& .legal-prose .legal-table tr:nth-of-type(even) td': { bgcolor: C.paper2 },

  // Definition list
  '& .legal-prose .legal-defs': {
    bgcolor: C.paper2,
    border: `1px solid ${C.line}`,
    p: '24px 28px',
    borderRadius: 1,
    m: 0,
    my: 2,
  },
  '& .legal-prose .legal-defs dt': {
    fontFamily: MONO,
    fontSize: 12,
    color: C.ink,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    mt: 1.5,
  },
  '& .legal-prose .legal-defs dt:first-of-type': { mt: 0 },
  '& .legal-prose .legal-defs dd': {
    m: 0, mt: 0.5,
    color: C.inkSoft,
    fontSize: 14.5,
  },

  // Callouts
  '& .legal-prose .legal-callout': {
    borderLeft: `4px solid ${C.teal}`,
    bgcolor: 'rgba(0,180,154,0.06)',
    p: '16px 20px',
    my: 2.5,
    borderRadius: '0 4px 4px 0',
  },
  '& .legal-prose .legal-callout.warn': {
    borderLeftColor: C.gold,
    bgcolor: 'rgba(240,180,41,0.08)',
  },
  '& .legal-prose .legal-callout p:last-child': { mb: 0 },
  '& .legal-prose .legal-callout-label': {
    fontFamily: MONO,
    fontSize: 10,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: C.ink,
    mb: 0.75,
    display: 'block',
  },
};

const PrivacyPolicy = () => {
  const { data, isLoading, error } = useGetPrivacyPolicyQuery();

  const sections = data?.sections || [];
  const officer = data?.grievance_officer || {};

  // Variables substituted into each section's markdown body.
  const vars = useMemo(() => ({
    company_name:      data?.company_name      || '',
    registered_office: data?.registered_office || '',
    cin:               data?.cin               || '',
  }), [data?.company_name, data?.registered_office, data?.cin]);

  return (
    <Box
      className="legal-shell"
      sx={{
        bgcolor: C.paper,
        color:   C.ink,
        // Each section sets its own scroll-margin-top so anchor links
        // clear the sticky navbar above it.
        ...proseSx,
      }}
    >
      {/* ── Hero ─────────────────────────────────────────────── */}
      <Box component="section" sx={phero}>
        <Box sx={{ ...wrap, ...pheroIn, gap: '14px' }}>
          <Box component="span" sx={eyebrowSx}>Legal · data protection</Box>
          <Typography component="h1" sx={hXl}>Privacy Policy.</Typography>

          {/* Document metadata — four bordered cards, as the reference has,
              so the version and review dates are visible without scrolling. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))',
              gap: '12px',
              mt: '10px',
            }}
          >
            {[
              ['Document ID',    data?.doc_id],
              ['Version',        data?.version],
              ['Effective date', toDisplayDate(data?.effective_date)],
              ['Last reviewed',  toDisplayDate(data?.last_reviewed)],
            ].map(([label, value]) => (
              <Box key={label} sx={{ ...cardSx, p: '16px 18px' }}>
                <Box
                  sx={{
                    fontFamily: UI, fontSize: 11.5, fontWeight: 700,
                    letterSpacing: '.1em', textTransform: 'uppercase',
                    color: 'var(--tl-muted)', mb: '6px',
                  }}
                >
                  {label}
                </Box>
                <Box sx={{ fontFamily: MONO, fontSize: 14.5, fontWeight: 600, color: 'var(--tl-g9)' }}>
                  {value || '—'}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>


      {/* ── Body: 12-col grid with sticky TOC ────────────────── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 }, px: { xs: 2.5, md: 4 } }}>
        {error && (
          <Box
            sx={{
              p: 4,
              border: `1px solid ${C.line}`,
              borderRadius: 1,
              bgcolor: '#fff',
              textAlign: 'center',
              color: C.inkSoft,
            }}
          >
            Couldn't load the policy — {error?.data?.message || 'please try again in a moment.'}
          </Box>
        )}

        {isLoading && (
          <Grid container spacing={{ xs: 4, md: 8 }} sx={{ width: '100%', m: 0, '& > .MuiGrid-item': { pl: 0 } }}>
            <Grid item xs={12} md={3}>
              <Skeleton variant="rounded" height={300} />
            </Grid>
            <Grid item xs={12} md={9}>
              <Skeleton variant="rounded" width="40%" height={32} sx={{ mb: 2 }} />
              <Skeleton variant="rounded" width="100%" height={18} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" width="95%" height={18} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" width="92%" height={18} sx={{ mb: 4 }} />
              <Skeleton variant="rounded" width="50%" height={28} sx={{ mb: 2 }} />
              <Skeleton variant="rounded" width="100%" height={120} />
            </Grid>
          </Grid>
        )}

        {!isLoading && !error && sections.length > 0 && (
          // No alignItems override — items stretch by default, which gives
          // the sticky TOC's parent column enough height to scroll within.
          <Grid container spacing={{ xs: 4, md: 8 }} sx={{ width: '100%', m: 0, '& > .MuiGrid-item': { pl: 0 } }}>
            {/* TOC */}
            <Grid item xs={12} md={3}>
              <Box
                component="nav"
                aria-label="Table of contents"
                sx={{
                  position: { md: 'sticky' },
                  top: { md: 88 },     // clears the sticky navbar
                  borderLeft: `2px solid ${C.line}`,
                  pl: 2.5,
                  maxHeight: { md: 'calc(100vh - 104px)' },
                  overflowY: { md: 'auto' },
                }}
              >
                <Box
                  sx={{
                    fontFamily: MONO,
                    fontSize: 10,
                    letterSpacing: '2px',
                    color: C.muted,
                    textTransform: 'uppercase',
                    mb: 2,
                  }}
                >
                  Contents
                </Box>
                <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                  {sections.map((s, idx) => (
                    <Box
                      component="li"
                      key={s.slug || idx}
                      sx={{
                        mb: 1.25,
                        fontSize: 13.5,
                        lineHeight: 1.4,
                        '& a': {
                          display: 'block',
                          color: C.inkSoft,
                          textDecoration: 'none',
                          py: 0.5,
                          transition: 'color 0.15s, padding-left 0.2s',
                        },
                        '& a::before': {
                          content: `"${String(idx + 1).padStart(2, '0')}"`,
                          fontFamily: MONO,
                          fontSize: 10,
                          color: C.gold,
                          mr: 1.25,
                          letterSpacing: '1px',
                        },
                        '& a:hover': {
                          color: C.teal,
                          pl: '4px',
                        },
                      }}
                    >
                      <a href={`#${s.slug}`}>{s.title}</a>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Article */}
            <Grid item xs={12} md={9}>
              <Box component="article" sx={{ maxWidth: 760 }}>
                {sections.map((s) => (
                  <PolicySection
                    key={s.slug}
                    section={s}
                    vars={vars}
                    officer={officer}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        )}

        {!isLoading && !error && sections.length === 0 && (
          <Box
            sx={{
              p: 4,
              border: `1px solid ${C.line}`,
              borderRadius: 1,
              bgcolor: '#fff',
              textAlign: 'center',
            }}
          >
            <Box sx={{ fontSize: 16, fontWeight: 700, color: C.ink, mb: 0.5 }}>
              Coming soon
            </Box>
            <Box sx={{ fontSize: 13, color: C.muted }}>
              Our team is finalising this document. Please check back shortly.
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
